// Domain · DprChip
//
// Tone- and label-coded renderer for the `request_dpr` flag on price
// lists and transactions. Tri-state:
//
//   - `true`  → green  · "Wnioskują"
//   - `false` → red    · "Nie wnioskują"
//   - `null`  → grey   · "Nie dotyczy"
//
// Variants mirror `EntityChip`:
//   - `"chip"` (default) — Pill chrome.
//   - `"inline"`         — inline tone-colored span.

import React from "react";
import { Pill, type PillSize } from "@ds/display/Pill";
import { TextValue } from "@ds/display/TextValue";
import { dprTone, dprText } from "@ds/domain/DprChip.helpers";


// ─── Inputs ───────────────────────────────────────────────────────────────

export interface DprChipInput {
  /**
   * `request_dpr` flag.
   * - `true`  → green  · "Wnioskują"
   * - `false` → red    · "Nie wnioskują"
   * - `null`  → grey   · "Nie dotyczy"
   */
  active: boolean | null | undefined;
  /** Rendering variant. Default `"chip"`. */
  variant?: "chip" | "inline";
  /** Pill size — only meaningful when `variant="chip"`. Default `"md"`. */
  size?: PillSize;
  /** `data-testid` on the root. Defaults to `"dpr-chip"`. */
  testId?: string;
}

export type DprChipProps = DprChipInput;

// ─── Component ────────────────────────────────────────────────────────────

export function DprChip({
  active,
  variant = "chip",
  size = "md",
  testId = "dpr-chip",
}: DprChipProps) {
  const tone = dprTone(active);
  const text = dprText(active);

  if (variant === "inline") {
    return (
      <TextValue tone={tone} testId={testId}>
        {text}
      </TextValue>
    );
  }

  return (
    <Pill tone={tone} size={size} testId={testId}>
      {text}
    </Pill>
  );
}
